import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BulkTravellerNamedList } from '@/components/Travellers/BulkTravellerNamedList';
import Button from '@/components/core/Button';
import type { BulkTravellerResult } from '@/helpers/bulkTravellerOperation';
import { IconWarningTriangle } from '@/icons';

interface BulkTravellerProgressProps {
  isProcessing: boolean;
  processedCount: number;
  progressTotal: number;
  currentName: string;
  remainingTimeLabel: string | null;
  showRemainingTime: boolean;
  results: BulkTravellerResult[] | null;
  progressAriaLabel: string;
  stayOnPageMessage: ReactNode;
  successSummary?: (count: number) => ReactNode;
  failureSummary?: (count: number) => ReactNode;
  onClose: () => void;
}

export const BulkTravellerProgress = ({
  isProcessing,
  processedCount,
  progressTotal,
  currentName,
  remainingTimeLabel,
  showRemainingTime,
  results,
  progressAriaLabel,
  stayOnPageMessage,
  successSummary,
  failureSummary,
  onClose,
}: BulkTravellerProgressProps) => {
  const { formatMessage } = useIntl();
  const isComplete = results !== null;
  const progressPercent =
    progressTotal === 0
      ? 0
      : Math.round((processedCount / progressTotal) * 100);
  const successCount =
    results?.filter((result) => result.status === 'success').length ?? 0;
  const failedResults =
    results?.filter((result) => result.status === 'failed') ?? [];

  return (
    <>
      <div className="flex flex-col gap-4" aria-busy={isProcessing}>
        <div className="flex flex-col gap-2">
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={progressTotal}
            aria-valuenow={processedCount}
            aria-valuetext={formatMessage(
              {
                defaultMessage: '{processed} av {total} resenärer',
              },
              { processed: processedCount, total: progressTotal },
            )}
            aria-label={progressAriaLabel}
            className="h-4 w-full overflow-hidden rounded-full bg-background-200"
          >
            <div
              className="h-full bg-primary-500 motion-safe:transition-[width] motion-safe:duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-text-800" aria-live="polite">
            {isProcessing ? (
              <FormattedMessage
                defaultMessage="Bearbetar {name} ({processed} av {total})"
                values={{
                  name: currentName,
                  processed: Math.min(processedCount + 1, progressTotal),
                  total: progressTotal,
                }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="{processed} av {total} resenärer behandlade"
                values={{
                  processed: processedCount,
                  total: progressTotal,
                }}
              />
            )}
          </p>
          {showRemainingTime && (
            <p className="text-text-700" aria-live="polite">
              {remainingTimeLabel}
            </p>
          )}
        </div>

        {isComplete && results && (
          <div className="flex flex-col gap-2">
            <p className="text-text-800">
              {successSummary ? (
                successSummary(successCount)
              ) : (
                <FormattedMessage
                  defaultMessage="{count, plural, one {# resenär uppdaterades} other {# resenärer uppdaterades}}"
                  values={{ count: successCount }}
                />
              )}
            </p>
            {failedResults.length > 0 && (
              <>
                <p className="text-text-800">
                  {failureSummary ? (
                    failureSummary(failedResults.length)
                  ) : (
                    <FormattedMessage
                      defaultMessage="{count, plural, one {# resenär misslyckades} other {# resenärer misslyckades}}"
                      values={{ count: failedResults.length }}
                    />
                  )}
                </p>
                <BulkTravellerNamedList
                  ariaLabel={formatMessage({
                    defaultMessage: 'Resenärer som misslyckades',
                  })}
                  items={failedResults.map((result) => ({
                    id: result.id,
                    name: result.name,
                    detail: result.message,
                  }))}
                />
              </>
            )}
            <div className="flex justify-end">
              <Button variant="filled" onClick={onClose}>
                <FormattedMessage defaultMessage="Stäng" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {isProcessing && (
        <div
          className="flex gap-2 rounded-md border border-yellow-500 bg-yellow-50 p-3 text-text-900"
          role="status"
        >
          <IconWarningTriangle
            className="size-5 shrink-0 text-yellow-700"
            aria-hidden="true"
          />
          <p>{stayOnPageMessage}</p>
        </div>
      )}
    </>
  );
};
